/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import _ from 'lodash';
import { connect } from 'react-redux';
import Dropdown from '../form/Dropdown';
import FlagButtons from '../form/FlagButtons';
import BottomBar from '../BottomBar';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { COUNTRY } from '../../../environment';
import { updateLanguages } from '../../../actions/preferences';

const PracticeDetails1 = ({
  intl,
  languages,
  // eslint-disable-next-line no-shadow
  updateLanguages,
  openLoadingPanel,
  closeLoadingPanel,
  previousPage,
  nextPage,
  errorCallback,
  currentSubStep,
  draftValues,
  onDraftChange,
}) => {
  const { control, handleSubmit, getValues } = useForm({
    defaultValues: draftValues || {
      languages: [],
      other_languages: [],
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const primaryLanguage = [
    {
      value: intl.formatMessage({ defaultMessage: 'English' }),
      label: intl.formatMessage({ defaultMessage: 'English' }),
    },
  ];

  const french = {
    value: intl.formatMessage({ defaultMessage: 'French' }),
    label: intl.formatMessage({ defaultMessage: 'French' }),
  };

  const spanish = {
    value: intl.formatMessage({ defaultMessage: 'Spanish' }),
    label: intl.formatMessage({ defaultMessage: 'Spanish' }),
  };

  const secondaryLanguage = COUNTRY === 'US' ? spanish : french;
  const mainLanguages = _.concat(primaryLanguage, secondaryLanguage);

  const sortedLanguages = _.sortBy(languages, language =>
    (language.label || '').toLowerCase(),
  );

  const onSubmit = async values => {
    const errorMessage = (
      <FormattedMessage defaultMessage="There was a problem updating your practice details." />
    );

    const mergedLanguages = values.other_languages?.length
      ? _.concat(
          values.languages,
          values.other_languages.map(language => language.value),
        )
      : values.languages;

    const languageValues = { languages: mergedLanguages };

    openLoadingPanel(
      <FormattedMessage defaultMessage="Updating practice details..." />,
    );

    try {
      await updateLanguages(languageValues);
      closeLoadingPanel();
      nextPage();
    } catch (error) {
      errorCallback(error, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>{intl.formatMessage({ defaultMessage: 'Practice Details' })}</h1>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <div className="sub-page-indicator">{`${currentSubStep + 1}/7`}</div>
      </div>
      <div style={{ marginBottom: '20px' }}>
        <h3>
          {intl.formatMessage({
            defaultMessage:
              'Which language(s) are you comfortable providing treatment in?',
          })}
        </h3>
        <p className="onboarding-label">
          {intl.formatMessage({
            defaultMessage:
              'Please select multiple if you are comfortable providing treatment in multiple languages.',
          })}
        </p>
      </div>

      <div className="input-row">
        <Controller
          name="languages"
          control={control}
          rules={{
            validate: value =>
              value && value.length > 0
                ? true
                : intl.formatMessage({
                    defaultMessage: 'Must select at least one language',
                  }),
          }}
          render={({ field, fieldState }) => (
            <FlagButtons
              {...field}
              options={mainLanguages}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      <div className="input-row">
        <Controller
          name="other_languages"
          control={control}
          render={({ field }) => (
            <Dropdown
              {...field}
              label={intl.formatMessage({ defaultMessage: 'Other languages' })}
              multi
              options={sortedLanguages}
              searchable
            />
          )}
        />
      </div>

      <BottomBar
        openLoadingPanel={openLoadingPanel}
        closeLoadingPanel={closeLoadingPanel}
        nextPage={nextPage}
        errorCallback={errorCallback}
        currentSubStep={currentSubStep}
        previousPage={previousPage}
      />
    </form>
  );
};

PracticeDetails1.propTypes = {
  languages: PropTypes.array.isRequired,
  updateLanguages: PropTypes.func.isRequired,
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  currentSubStep: PropTypes.number.isRequired,
  intl: PropTypes.object.isRequired,
  previousPage: PropTypes.func.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default injectIntl(connect(null, { updateLanguages })(PracticeDetails1));
